import { deepCloneObject } from '../../../../store/utility';

/**
 * Calculates the maximum number of action cells configured in @param rows array.
 * @function
 * @param {Object[]} rows array of objects to iterate.
 * @returns {Number} maximum number of action cells configured.
 */
const getActionCellMax = rows => {
	return rows && Array.isArray(rows)
		? rows.reduce((max, row) => {
				return Math.max(
					max,
					row.ActionCells ? row.ActionCells.length : 0
				);
		  }, 0)
		: 0;
};

/**
 * Sorts rows by specified header according to specified order.
 * @param {Array} rows array of objects.
 * @param {String} header key of cell to sort.
 * @param {Number} order sort order; 1 for ascending, -1 for descending.
 * @returns {Array} sorted rows.
 */
const sortByHeader = (rows, header, order) => {
	const ascending = order > 0;
	return rows.sort((firstEl, secondEl) => {
		if (
			firstEl.Cells &&
			secondEl.Cells &&
			firstEl.Cells[header] &&
			secondEl.Cells[header]
		) {
			const valueFirstEl = firstEl.Cells[header].Value
				? (header.toLowerCase().includes('date') ? true : false)
					? new Date(firstEl.Cells[header].Value)
					: firstEl.Cells[header].Value.toLowerCase()
				: '';
			const valueSecondEl = secondEl.Cells[header].Value
				? (header.toLowerCase().includes('date') ? true : false)
					? new Date(secondEl.Cells[header].Value)
					: secondEl.Cells[header].Value.toLowerCase()
				: '';
			if (valueFirstEl < valueSecondEl) {
				return ascending ? -1 : 1;
			}
			if (valueFirstEl > valueSecondEl) {
				return ascending ? 1 : -1;
			}
		}
		return 0;
	});
};

/**
 * Flips `Checked` property of header action at index @param actionIndex found in @param actions.
 * Useful in handling header action cell changes.
 *
 * @function
 * @param {Object[]} actions array of base actions.
 * @param {Number} actionIndex index of action of interest.
 * @returns {Object[]} updated array of base actions.
 */
const toggleHeaderActionCell = (actions, actionIndex) => {
	let actionsCopy = [];
	if (Array.isArray(actions)) {
		actionsCopy = deepCloneObject(actions);
		if (
			actionIndex !== null &&
			actionsCopy.length - 1 >= actionIndex &&
			actionsCopy[actionIndex].Header
		) {
			actionsCopy[actionIndex].Header.Checked =
				!actionsCopy[actionIndex].Header.Checked;
		}
	}

	return actionsCopy;
};

/**
 * Flips `Checked` property of action cell bound to @param actionIndex defined in @param row found in @param rows.
 * Useful in handling non-header action cell changes.
 *
 * @function
 * @param {Object[]} rows array of objects.
 * @param {Object} row object of interest.
 * @param {Number} actionIndex bound index of base object defining row action cell action in `actions`.
 * @returns {Object[]} array of objects with updated object.
 */
const toggleRowActionCell = (rows, row, actionIndex) => {
	let rowsCopy = [];
	if (Array.isArray(rows)) {
		rowsCopy = deepCloneObject(rows);
		if (typeof row === 'object') {
			let rowIndex = rowsCopy.findIndex(
				rowCopy => rowCopy.Key === row.Key
			);
			if (
				rowIndex !== null &&
				rowIndex > -1 &&
				rowsCopy[rowIndex].ActionCells
			) {
				let cellIndex = rowsCopy[rowIndex].ActionCells.findIndex(
					cell => cell.BindTo === actionIndex
				);
				if (cellIndex > -1) {
					rowsCopy[rowIndex].ActionCells[cellIndex].Checked =
						!rowsCopy[rowIndex].ActionCells[cellIndex].Checked;
				}
			}
		}
	}

	return rowsCopy;
};

/**
 * Flips `Checked` property of all action cells bound to @param actionIndex found in @param rows.
 * Useful in handling header action cell changes.
 *
 * @function
 * @param {Object[]} rows array of objects.
 * @param {Number} actionIndex bound index of base object defining row action cell action in `actions`.
 * @param {Event} e raised event.
 * @returns {Object[]} array of objects with updated object(s).
 */
const toggleRowsActionCell = (rows, actionIndex, e) => {
	let rowsCopy = [];
	if (Array.isArray(rows)) {
		const checked =
			e &&
			typeof e === 'object' &&
			e.target != null &&
			e.target.checked === true;
		rowsCopy = deepCloneObject(rows);

		rowsCopy.forEach(row => {
			if (row.ActionCells && actionIndex !== null) {
				const cellIndex = row.ActionCells.findIndex(
					actionCell => actionCell.BindTo === actionIndex
				);
				// if row possesses target action and outdated `Checked` value
				if (
					cellIndex > -1 &&
					row.ActionCells[cellIndex].Checked !== checked
				) {
					row.ActionCells[cellIndex].Checked = checked;
				}
			}
		});
	}

	return rowsCopy;
};

export {
	getActionCellMax,
	sortByHeader,
	toggleHeaderActionCell,
	toggleRowActionCell,
	toggleRowsActionCell,
};
